import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Alert, Box, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import AdminBillingView from '../../Admin/Billing/views/AdminBillingView';
import AdminOpcosView from '../../Admin/Opcos/views/AdminOpcosView';
import AdminOperationsView from '../../Admin/Operations/views/AdminOperationsView';
import OperationsDetailView from '../../Admin/Operations/views/OperationsDetailView';
import AdminPackagesView from '../../Admin/Packages/views/AdminPackagesView';
import PackageAccessView from '../../Admin/Packages/views/PackageAccessView';
import PackageEditView from '../../Admin/Packages/views/PackageEditView';
import PackageHistoryView from '../../Admin/Packages/views/PackageHistoryView';
import AdminReportsView from '../../Admin/Reports/views/AdminReportsView';
import ReportDetailView from '../../Admin/Reports/views/ReportDetailView';
import AdminToolView from '../../Admin/Tools/views/AdminToolView';
import {
    fetchPackageRoles,
    fetchToolOwnerTools,
    selectCanCreateProject,
    selectHeiUser,
} from '../../Auth/store/accountSlice';
import { resolveLoggedUserFromBackend } from '../../Auth/utils/jwtUtils';
import routes from '../../Common/consts/routes';
import CreateFaqArticle from '../../Faq/CreateFaqArticle/views/CreateFaqArticle';
import FaqArticle from '../../Faq/FaqArticle/views/FaqArticle';
import FaqList from '../../Faq/FaqList/views/FaqList';
import BaseLayout from '../../Layout/components/BaseLayout/BaseLayout';
import EditProfileView from '../../Profile/views/EditProfileView';
import CreateProject from '../../Projects/CreateProject/views/CreateProject/CreateProject';
import ProjectDetails from '../../Projects/ProjectDetails/views/ProjectDetails/ProjectDetails';
import ProjectList from '../../Projects/ProjectsList/views/ProjectList';
import Requests from '../../Projects/SelfOnboarding/views/Requests';
import { useAppEntry } from '../hooks/useAppEntry';
import { AppDispatch } from '../store/store';
import './AppEntry.scss';

interface AppEntryProps {
    appInsights: ApplicationInsights;
}

const AppEntry = ({ appInsights }: AppEntryProps) => {
    const { tokenSetup, identityResolutionError } = useAppEntry();
    const dispatch = useDispatch<AppDispatch>();
    const heiUser = useSelector(selectHeiUser);
    const canCreateProject = useSelector(selectCanCreateProject);

    const isAdmin = useMemo(() => heiUser.roles.some((r) => r === 'Admin'), [heiUser]);
    const resolvedOidRef = useRef('');

    useEffect(() => {
        if (tokenSetup && heiUser.oid) {
            appInsights.setAuthenticatedUserContext(heiUser.oid, heiUser.email);

            dispatch(fetchToolOwnerTools());
            dispatch(fetchPackageRoles());

            if (resolvedOidRef.current !== heiUser.oid) {
                resolvedOidRef.current = heiUser.oid;
                resolveLoggedUserFromBackend();
            }
        }
    }, [appInsights, heiUser.email, heiUser.oid, tokenSetup]);

    if (tokenSetup && identityResolutionError) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant='h5' sx={{ mb: 2 }}>
                    Sign-in issue
                </Typography>
                <Alert severity='error'>{identityResolutionError}</Alert>
            </Box>
        );
    }

    return tokenSetup ? (
        <BaseLayout>
            <Routes>
                <Route path={routes.projects} element={<ProjectList />} />
                {canCreateProject && (
                    <Route path={routes.createProject} element={<CreateProject />} />
                )}
                <Route path={routes.projectDetails_v1} element={<ProjectDetailsRedirect />} />
                <Route path={routes.projectDetails} element={<ProjectDetails />} />
                <Route path={routes.faq} element={<FaqList />} />
                <Route path={routes.faqArticle} element={<FaqArticle />} />
                <Route path={routes.createFaqArticle} element={<CreateFaqArticle />} />
                <Route path={routes.requests} element={<Requests />} />
                <Route path={routes.editProfile} element={<EditProfileView />} />
                {isAdmin && (
                    <>
                        <Route path={routes.admin.billing} element={<AdminBillingView />} />
                        <Route path={routes.admin.opcos} element={<AdminOpcosView />} />
                        <Route path={routes.admin.tools} element={<AdminToolView />} />
                        <Route path={routes.admin.reports} element={<AdminReportsView />} />
                        <Route path={routes.admin.reportDetail} element={<ReportDetailView />} />
                        <Route path={routes.admin.operations} element={<AdminOperationsView />} />
                        <Route
                            path={routes.admin.operationsFigmaCleanup}
                            element={<AdminOperationsView />}
                        />
                        <Route
                            path={routes.admin.operationsUserSearch}
                            element={<AdminOperationsView />}
                        />
                        <Route
                            path={routes.admin.operationsToolSearch}
                            element={<AdminOperationsView />}
                        />
                        <Route
                            path={routes.admin.operationsToolsUsersSync}
                            element={<AdminOperationsView />}
                        />
                        <Route
                            path={routes.admin.operationsBanners}
                            element={<AdminOperationsView />}
                        />
                        <Route
                            path={routes.admin.operationsJiraWorkflows}
                            element={<AdminOperationsView />}
                        />
                        <Route
                            path={routes.admin.operationsDetail}
                            element={<OperationsDetailView />}
                        />
                    </>
                )}
                {/* Package routes accessible by admins, owners, and editors */}
                <Route path={routes.admin.packages} element={<AdminPackagesView />} />
                <Route path={routes.admin.packageEdit} element={<PackageEditView />} />
                <Route path={routes.admin.packageHistory} element={<PackageHistoryView />} />
                <Route path={routes.admin.packageAccess} element={<PackageAccessView />} />
            </Routes>
        </BaseLayout>
    ) : (
        <></>
    );
};

const ProjectDetailsRedirect = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => navigate(`/project/${id}/details`), [id]);
    return null;
};

export default AppEntry;
